import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { INPUT_CLS, ERROR_CLS, LABEL_CLS } from '@/components/brand/BrandKit';
import { AuthShell, AuthLink, Field, PasswordField } from '@/components/account/AuthShell';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { uaepassTicket, uaepassLinkExisting, uaepassRegister, apiError, BRAND_ACCOUNT } from '@/api/account';

function Identity({ profile }) {
  const rows = [['Name', profile.name], ['Emirates ID', profile.idn_masked || '—'], ['Nationality', profile.nationality_en || '—'], ['Mobile', profile.mobile || '—'], ['Email', profile.email || '—']];
  return (
    <div data-testid="uaepass-identity" className="rounded-xl border border-black/10 bg-[#FAFAFA] p-4">
      <p className="flex items-center gap-2 font-heading font-bold text-xs uppercase tracking-wider text-[#121212]"><ShieldCheck className="w-4 h-4 text-[#EE5A01]" /> {profile.verified ? 'Verified by UAE PASS' : 'UAE PASS profile (basic, not yet verified)'}</p>
      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {rows.map(([k, v]) => (<div key={k} className="flex justify-between gap-3 font-body text-sm"><dt className="text-[#888]">{k}</dt><dd className="text-[#121212] font-medium text-right truncate" dir="ltr">{v}</dd></div>))}
      </dl>
    </div>
  );
}

function LinkExisting({ ticket, onDone }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [state, setState] = useState({ error: '', busy: false });
  const submit = async (e) => {
    e.preventDefault();
    setState({ error: '', busy: true });
    try { onDone(await uaepassLinkExisting({ ticket, ...form })); } catch (err) { setState({ error: apiError(err), busy: false }); }
  };
  return (
    <form onSubmit={submit} data-testid="uaepass-link-form" className="space-y-4">
      <p className="font-body text-sm text-[#666666]">Sign in once with your existing email and password. Your UAE PASS identity will be linked permanently.</p>
      {state.error && <p data-testid="uaepass-link-error" role="alert" className={ERROR_CLS}>{state.error}</p>}
      <Field label="Email" htmlFor="uaepass-link-email"><Input id="uaepass-link-email" data-testid="uaepass-link-email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className={INPUT_CLS} required /></Field>
      <Field label="Password" htmlFor="uaepass-link-password"><PasswordField id="uaepass-link-password" testid="uaepass-link-password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} /></Field>
      <button type="submit" data-testid="uaepass-link-submit" disabled={state.busy || !form.email || !form.password} className="btn-primary w-full disabled:opacity-50">{state.busy ? 'Linking…' : 'Sign in and link UAE PASS'}</button>
    </form>
  );
}

function NewAccount({ ticket, profile, initialType, onDone, onPending }) {
  const [type, setType] = useState(initialType);
  const [form, setForm] = useState({ company: '', trade_license: '', job_title: '', mobile: profile.mobile || '' });
  const [state, setState] = useState({ error: '', busy: false });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const corporate = type === 'corporate';
  const submit = async (e) => {
    e.preventDefault();
    setState({ error: '', busy: true });
    try {
      const res = await uaepassRegister({ ticket, account_type: type, ...form });
      if (res.status === 'pending') onPending(res.email); else onDone(res);
    } catch (err) { setState({ error: apiError(err), busy: false }); }
  };
  return (
    <form onSubmit={submit} data-testid="uaepass-register-form" className="space-y-4">
      <div>
        <span className={LABEL_CLS}>Account type</span>
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#F5F5F3] p-1.5">
          {[['retail', 'Personal · Retail'], ['corporate', 'Corporate · Business']].map(([id, label]) => (
            <button key={id} type="button" data-testid={`uaepass-type-${id}`} onClick={() => setType(id)} className={`rounded-lg px-3 py-2.5 font-heading font-bold text-sm transition-colors ${type === id ? 'bg-white shadow-sm text-[#121212]' : 'text-[#666666]'}`}>{label}</button>
          ))}
        </div>
      </div>
      <p className="font-body text-xs text-[#888]">Name, Emirates ID, email{profile.mobile ? ' and mobile' : ''} come from UAE PASS and cannot be edited. No password is needed — you will always sign in with UAE PASS.</p>
      {!profile.mobile && <Field label="Mobile number" htmlFor="uaepass-mobile"><Input id="uaepass-mobile" data-testid="uaepass-mobile" type="tel" value={form.mobile} onChange={set('mobile')} placeholder="+971 50 123 4567" className={INPUT_CLS} dir="ltr" required /></Field>}
      {corporate && (
        <>
          <Field label="Company name" htmlFor="uaepass-company"><Input id="uaepass-company" data-testid="uaepass-company" value={form.company} onChange={set('company')} placeholder="Company LLC" className={INPUT_CLS} required /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Trade licence no. (optional)" htmlFor="uaepass-trade-license"><Input id="uaepass-trade-license" data-testid="uaepass-trade-license" value={form.trade_license} onChange={set('trade_license')} className={INPUT_CLS} /></Field>
            <Field label="Job title (optional)" htmlFor="uaepass-job-title"><Input id="uaepass-job-title" data-testid="uaepass-job-title" value={form.job_title} onChange={set('job_title')} className={INPUT_CLS} /></Field>
          </div>
        </>
      )}
      {state.error && <p data-testid="uaepass-register-error" role="alert" className={ERROR_CLS}>{state.error}</p>}
      <button type="submit" data-testid="uaepass-register-submit" disabled={state.busy || (corporate && !form.company) || (!profile.mobile && !form.mobile)} className="btn-primary w-full disabled:opacity-50">
        {state.busy ? 'Creating…' : corporate ? 'Request corporate account' : `Create my ${BRAND_ACCOUNT} account`}
      </button>
    </form>
  );
}

export default function UaePassCompletePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setCustomer } = useCustomerAuth();
  const ticket = params.get('ticket') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState('');
  const [pending, setPending] = useState('');

  useEffect(() => { document.title = `Complete your UAE PASS sign-in | ${BRAND_ACCOUNT}`; }, []);
  useEffect(() => {
    if (!ticket) { setError('Missing UAE PASS session. Please sign in with UAE PASS again.'); return; }
    uaepassTicket(ticket).then(setData).catch((err) => setError(apiError(err)));
  }, [ticket]);

  const done = (user) => { setCustomer(user); navigate('/account', { replace: true, state: { isNew: true } }); };

  const body = () => {
    if (error) return <p data-testid="uaepass-complete-error" role="alert" className={ERROR_CLS}>{error}</p>;
    if (!data) return <p className="font-body text-sm text-[#666666]">Loading your UAE PASS profile…</p>;
    if (pending) return (
      <div data-testid="uaepass-pending" className="text-center py-4">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#FFF4ED] text-[#EE5A01]"><Clock className="w-8 h-8" /></span>
        <h2 className="mt-5 font-heading font-black text-2xl text-[#121212]">Request received</h2>
        <p className="mt-2 font-body text-sm text-[#666666]">Corporate accounts are verified by our team before activation. We will email <b>{pending}</b> once approved.</p>
      </div>
    );
    return (
      <div className="space-y-6">
        <Identity profile={data.profile} />
        {!choice && (
          <div data-testid="uaepass-choice">
            <p className="font-heading font-bold text-base text-[#121212]">Do you already have a {BRAND_ACCOUNT} account?</p>
            <p className="mt-1 font-body text-xs text-[#888]">We could not find an account matching this UAE PASS identity.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button type="button" data-testid="uaepass-choice-yes" onClick={() => setChoice('link')} className="btn-secondary w-full">Yes, link my account</button>
              <button type="button" data-testid="uaepass-choice-no" onClick={() => setChoice('new')} className="btn-primary w-full">No, create one</button>
            </div>
          </div>
        )}
        {choice === 'link' && <LinkExisting ticket={ticket} onDone={done} />}
        {choice === 'new' && <NewAccount ticket={ticket} profile={data.profile} initialType={params.get('type') === 'corporate' ? 'corporate' : (data.account_type || 'retail')} onDone={done} onPending={setPending} />}
        {choice && <button type="button" data-testid="uaepass-choice-back" onClick={() => setChoice('')} className="font-heading font-bold text-xs text-[#888] hover:text-[#EE5A01]">← Back</button>}
      </div>
    );
  };

  return (
    <AuthShell testid="uaepass-complete-page" eyebrow="UAE PASS" title="Almost there" lead="Your identity was confirmed by UAE PASS. Let’s connect it to your account."
      footer={<AuthLink to="/login" testid="uaepass-back-to-login">Back to sign in</AuthLink>}>
      {body()}
    </AuthShell>
  );
}
